import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Progress, 
  Button, 
  Tag, 
  Space, 
  Statistic, 
  Row, 
  Col, 
  Timeline,
  Alert,
  Tooltip
} from 'antd';
import {
  CrownOutlined,
  RocketOutlined,
  StarOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import usePlan from '../../hooks/usePlan';
import PlanBadge from '../common/PlanBadge';
import UpgradeModal from './UpgradeModal';
import { PLANS, PLAN_FEATURES } from '../../services/planService';
import { subscriptionApi } from '../../utils/api';

const { Title, Text, Paragraph } = Typography;

/**
 * Composant pour gérer l'abonnement utilisateur
 */
const SubscriptionManagement = () => {
  const {
    planData,
    currentPlan,
    planName,
    isTrialPlan,
    trialMessagesUsed,
    trialMaxMessages,
    loading,
    checkMessageLimit,
    refreshPlan
  } = usePlan();

  const [usageStats, setUsageStats] = useState({
    messages: { used: 0, total: 0 },
    products: { used: 0, total: 0 },
    shops: { used: 0, total: 0 }
  });
  const [billingHistory, setBillingHistory] = useState([]);
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);

  useEffect(() => {
    loadUsageStats();
    loadBillingHistory();
  }, [planData]);

  /**
   * Charger les statistiques d'utilisation
   */
  const loadUsageStats = async () => {
    try {
      // Vérifier les messages
      const messageLimit = await checkMessageLimit();
      
      // Récupérer les statistiques d'usage via l'API
      const usageStatsResponse = await subscriptionApi.getUsageStats();
      const usageData = usageStatsResponse?.data?.data || {};
      
      const planConfig = PLAN_FEATURES[currentPlan];

      setUsageStats({
        messages: {
          used: usageData.messagesUsed || messageLimit?.used || trialMessagesUsed || 0, // Priorité à l'API
          total: messageLimit?.total || trialMaxMessages || planConfig?.maxMessagesPerMonth || 0
        },
        products: {
          used: usageData.productsCount || 0, // Récupéré via API
          total: planConfig?.maxProducts || 0
        },
        shops: {
          used: usageData.shopsCount || 0, // Récupéré via API
          total: planConfig?.maxShops || 0
        }
      });
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      
      // Fallback avec les valeurs précédentes en cas d'erreur API
      const planConfig = PLAN_FEATURES[currentPlan];
      setUsageStats({
        messages: {
          used: trialMessagesUsed || 0,
          total: trialMaxMessages || planConfig?.maxMessagesPerMonth || 0
        },
        products: {
          used: 0, // Valeur par défaut
          total: planConfig?.maxProducts || 0
        },
        shops: {
          used: 1, // Valeur par défaut
          total: planConfig?.maxShops || 0
        }
      });
    }
  };

  /**
   * Charger l'historique de facturation
   */
  const loadBillingHistory = async () => {
    try {
      const response = await subscriptionApi.getBillingHistory();
      if (response.data.success) {
        setBillingHistory(response.data.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
      // Fallback avec données vides en cas d'erreur
      setBillingHistory([]);
    }
  };

  /**
   * Gérer la mise à niveau du plan
   */
  const handleUpgrade = async (newPlan, updatedData) => {
    try {
      
      // Recharger les données du plan depuis le hook
      await refreshPlan();
      
      // Recharger les statistiques d'utilisation
      await loadUsageStats();
      
      // Recharger l'historique de facturation
      loadBillingHistory();
      
    } catch (error) {
      console.error('Erreur lors de la mise à jour après upgrade:', error);
    }
  };

  /**
   * Obtenir l'icône du plan
   */
  const getPlanIcon = (plan) => {
    const icons = {
      [PLANS.DECOUVERTE]: <InfoCircleOutlined />,
      [PLANS.ESSENTIEL]: <RocketOutlined />,
      [PLANS.CROISSANCE]: <StarOutlined />,
      [PLANS.EXCELLENCE]: <CrownOutlined />
    };
    return icons[plan] || <InfoCircleOutlined />;
  };

  /**
   * Obtenir la couleur du plan
   */
  const getPlanColor = (plan) => {
    const colors = {
      [PLANS.DECOUVERTE]: 'default',
      [PLANS.ESSENTIEL]: 'blue',
      [PLANS.CROISSANCE]: 'cyan',
      [PLANS.EXCELLENCE]: 'purple'
    };
    return colors[plan] || 'default';
  };

  /**
   * Calculer le pourcentage d'utilisation
   */
  const getUsagePercentage = (used, total) => {
    if (total === -1) return 0; // Illimité
    if (total === 0) return 0;
    return Math.round((used / total) * 100);
  };

  /**
   * Formater les dates
   */
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec badge du plan */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-2xl">
              {getPlanIcon(currentPlan)}
            </div>
            <div>
              <Title level={4} className="mb-0">Mon Abonnement</Title>
              <PlanBadge showProgress={false} showUpgradeLink={false} />
            </div>
          </div>
          
          {currentPlan !== PLANS.EXCELLENCE && (
            <Button 
              type="primary" 
              icon={<ArrowUpOutlined />}
              onClick={() => setUpgradeModalVisible(true)}
            >
              Mettre à niveau
            </Button>
          )}
        </div>

        {/* Détails du plan */}
        <div className="mt-4">
          <Row gutter={16}>
            <Col span={8}>
              <Statistic 
                title="Plan actuel" 
                value={planName}
                prefix={getPlanIcon(currentPlan)}
              />
            </Col>
            <Col span={8}>
              <Statistic 
                title="Date de début"
                value={planData?.plan_start_date ? formatDate(planData.plan_start_date) : 'N/A'}
                prefix={<CalendarOutlined />}
              />
            </Col>
            <Col span={8}>
              <Statistic 
                title="Statut"
                value={isTrialPlan ? 'Version d\'essai' : 'Actif'}
                valueStyle={{ color: isTrialPlan ? '#faad14' : '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
          </Row>
        </div>
      </Card>

      {/* Alerte d'essai */}
      {isTrialPlan && (
        <Alert
          message="Version d'essai active"
          description={`Vous utilisez actuellement la version d'essai. ${trialMaxMessages - trialMessagesUsed} messages restants.`}
          type="warning"
          showIcon
          action={
            <Button 
              size="small" 
              type="primary"
              onClick={() => setUpgradeModalVisible(true)}
            >
              Voir les plans
            </Button>
          }
        />
      )}

      {/* Statistiques d'utilisation */}
      <Card title="Utilisation actuelle" extra={
        <Tooltip title="Rafraîchir les données">
          <Button 
            icon={<InfoCircleOutlined />} 
            size="small" 
            onClick={refreshPlan}
          >
            Actualiser
          </Button>
        </Tooltip>
      }>
        <Row gutter={[16, 16]}>
          {/* Messages */}
          <Col xs={24} sm={8}>
            <Card size="small" className="text-center">
              <Statistic
                title="Messages ce mois"
                value={usageStats.messages.used}
                suffix={usageStats.messages.total === -1 ? "/ Illimité" : `/ ${usageStats.messages.total}`}
              />
              {usageStats.messages.total !== -1 && (
                <Progress
                  percent={getUsagePercentage(usageStats.messages.used, usageStats.messages.total)}
                  status={getUsagePercentage(usageStats.messages.used, usageStats.messages.total) >= 90 ? "exception" : "normal"}
                  className="mt-2"
                />
              )}
            </Card>
          </Col>

          {/* Produits */}
          <Col xs={24} sm={8}>
            <Card size="small" className="text-center">
              <Statistic
                title="Produits"
                value={usageStats.products.used}
                suffix={usageStats.products.total === -1 ? "/ Illimité" : `/ ${usageStats.products.total}`}
              />
              {usageStats.products.total !== -1 && (
                <Progress
                  percent={getUsagePercentage(usageStats.products.used, usageStats.products.total)}
                  status={getUsagePercentage(usageStats.products.used, usageStats.products.total) >= 90 ? "exception" : "normal"}
                  className="mt-2"
                />
              )}
            </Card>
          </Col>

          {/* Boutiques */}
          <Col xs={24} sm={8}>
            <Card size="small" className="text-center">
              <Statistic
                title="Boutiques"
                value={usageStats.shops.used}
                suffix={usageStats.shops.total === -1 ? "/ Illimité" : `/ ${usageStats.shops.total}`}
              />
              {usageStats.shops.total !== -1 && (
                <Progress
                  percent={getUsagePercentage(usageStats.shops.used, usageStats.shops.total)}
                  status={getUsagePercentage(usageStats.shops.used, usageStats.shops.total) >= 90 ? "exception" : "normal"}
                  className="mt-2"
                />
              )}
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Fonctionnalités du plan */}
      <Card title="Fonctionnalités incluses">
        <div className="space-y-2">
          {PLAN_FEATURES[currentPlan]?.features.map((feature, index) => (
            <div key={index} className="flex items-center space-x-2">
              <CheckCircleOutlined className="text-green-500" />
              <Text>{feature.replace(/_/g, ' ')}</Text>
            </div>
          ))}
        </div>
        
        <div className="mt-4">
          <Link to="/pricing">
            <Button type="link" className="p-0">
              Voir toutes les fonctionnalités disponibles →
            </Button>
          </Link>
        </div>
      </Card>

      {/* Historique de facturation */}
      <Card title="Historique de facturation">
        {billingHistory.length > 0 ? (
          <Timeline>
            {billingHistory.map((item) => (
              <Timeline.Item 
                key={item.id}
                dot={<DollarOutlined />}
                color={item.status === 'active' ? 'green' : 'gray'}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <Text strong>{item.plan}</Text>
                    <div className="text-sm text-gray-500">
                      {formatDate(item.date)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">
                      {item.amount === 0 ? 'Gratuit' : `${item.amount.toLocaleString()} XOF`}
                    </div>
                    <Tag color={item.status === 'active' ? 'green' : 'default'}>
                      {item.type === 'trial' ? 'Essai' : 'Payé'}
                    </Tag>
                  </div>
                </div>
              </Timeline.Item>
            ))}
          </Timeline>
        ) : (
          <Text className="text-gray-500">Aucun historique de facturation disponible.</Text>
        )}
      </Card>

      {/* Actions rapides */}
      <Card title="Actions rapides">
        <Space wrap>
          <Button 
            icon={<ArrowUpOutlined />}
            onClick={() => setUpgradeModalVisible(true)}
          >
            Changer de plan
          </Button>
          
          {currentPlan !== PLANS.DECOUVERTE && (
            <Button icon={<DollarOutlined />} disabled>
              Méthodes de paiement (Bientôt)
            </Button>
          )}
          
          <Button icon={<InfoCircleOutlined />} disabled>
            Support client (Bientôt)
          </Button>
        </Space>
      </Card>

      {/* Modal de mise à niveau */}
      <UpgradeModal
        visible={upgradeModalVisible}
        onCancel={() => setUpgradeModalVisible(false)}
        currentPlan={currentPlan}
        onUpgrade={handleUpgrade}
      />
    </div>
  );
};

export default SubscriptionManagement; 